import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import { AvatarGroup } from '@mui/material';
import { format } from 'date-fns';
import { ReactElement, SyntheticEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { CategoryListItemExt, PostListItem, useTargetDetailsQuery } from 'api';
import birthdayPost from 'assets/images/birthdayPost.png';
import welcomePost from 'assets/images/welcomePost.png';
import {
  Avatar,
  VideoGallery,
  ListItemImageAttachment,
  SeeMoreModal,
  Spinner,
  VideoAttachment,
  ImageGallery,
  DocumentAttachment,
} from 'components';
import { ReactionsWrapper } from 'components/ReactionsWrapper';
import {
  MONTH_DAY_SHORT,
  DETAILS_FILE_NAME_LENGTH_LIMIT,
  TIME_DATE_FORMAT_WITH_COMMA,
  TIME_DATE_MONTH_WITH_COMMA,
} from 'constants/index';
import { TargetItem } from 'features/kpi';
import { CreateButton } from 'features/kpi/containers/KpiPreview/styled';
import { usePostDetailsPage } from 'features/postDetails/containers/PostDetailsPage/usePostDetailsPage';
import { PollOptions } from 'features/postsList/components/PostListItem/PollOptions';
import { PollResults } from 'features/postsList/components/PostListItem/PollResults';
import { ReactionConfirmDialog } from 'features/postsList/components/ReactionConfirmDialog';
import { useModal, useWindowSize } from 'hooks';
import { Links } from 'settings';
import { theme } from 'styles';
import { InstanceType } from 'types';
import { formattedDate, getFullName, hideDocNameMidPart } from 'utils';

import {
  AttachmentsW,
  AuthorInfo,
  AuthorMeta,
  AuthorName,
  AuthorSection,
  BirthdayImage,
  ContainerW,
  DateTime,
  DescriptionW,
  DetailsCard,
  DocsGridW,
  FooterW,
  LinkW,
  MediaColumnW,
  MediaSectionW,
  MoreBadge,
  PollDescription,
  PostActionsW,
  PostTitle,
  SectionLabel,
  SectionW,
  SeeMoreLink,
  Tag,
  TagContainer,
  TagW,
  TargetW,
  TaskFieldLabel,
  TaskFieldSection,
  TaskFieldValue,
  TaskPriorityChip,
  WelcomeImage,
  AssigneeListW,
  AssigneeRow,
  ProgressBarContainer,
  ProgressFill,
  ProgressLabel,
  ProgressTrack,
} from './styled';
import { attachmentStyles, attachmentMobileStyles } from './styles';

const MOBILE_CATEGORIES_LIMIT = 2;
const MOBILE_DOCS_LIMIT = 2;
const MOBILE_IMAGES_LIMIT = 8;
const MOBILE_VIDEOS_LIMIT = 4;

interface PostDetailsProps {
  post: PostListItem;
  categoriesList: CategoryListItemExt[];
  isWelcomePost: boolean;
  isBirthdayPost: boolean;
  isPollPost: boolean;
  isKpiTargetPost: boolean;
  targetId?: string;
  title?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  showDeleteButton?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function PostDetails({
  post,
  categoriesList,
  isWelcomePost,
  isKpiTargetPost,
  isBirthdayPost,
  isPollPost,
  targetId = '',
  title,
  canEdit,
  canDelete,
  showDeleteButton,
  onEdit,
  onDelete,
}: PostDetailsProps): ReactElement {
  const {
    isOpen: isOpenGallery,
    handleOpenModal: handleOpenModalGallery,
    handleCloseModal: handleCloseModalGallery,
  } = useModal();

  const {
    isOpen: isOpenVideoGallery,
    handleOpenModal: handleOpenModalVideoGallery,
    handleCloseModal: handleCloseModalVideoGallery,
  } = useModal();

  const {
    isOpen: isOpenReaction,
    handleOpenModal: handleOpenModalReaction,
    handleCloseModal: handleCloseModalReaction,
  } = useModal();

  const {
    isOpen: isOpenCategoriesModal,
    handleOpenModal: handleOpenCategoriesModal,
    handleCloseModal: handleCloseCategoriesModal,
  } = useModal();

  const {
    isOpen: isOpenDocsModal,
    handleOpenModal: handleOpenDocsModal,
    handleCloseModal: handleCloseDocsModal,
  } = useModal();

  const {
    isOpen: isOpenImagesModal,
    handleOpenModal: handleOpenImagesModal,
    handleCloseModal: handleCloseImagesModal,
  } = useModal();

  const {
    isOpen: isOpenVideosModal,
    handleOpenModal: handleOpenVideosModal,
    handleCloseModal: handleCloseVideosModal,
  } = useModal();

  const {
    isOpen: isOpenAssigneesModal,
    handleOpenModal: handleOpenAssigneesModal,
    handleCloseModal: handleCloseAssigneesModal,
  } = useModal();

  const { isMobile } = useWindowSize();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoading, data } = useTargetDetailsQuery(targetId);

  const isTaskPost = post.type === InstanceType.tasks;

  const { description, attachments, id, author, published_at = '', poll, reactions_stats } = post;
  const { first_name, last_name, id: authorId, avatar } = author;
  const imagesList = attachments?.filter((attachment) => attachment.type === 'image') || [];
  const videosList = attachments?.filter((attachment) => attachment.type === 'video') || [];
  const docsList = attachments?.filter((attachment) => attachment.type === 'document') || [];
  const dateFormat =
    isMobile && !isKpiTargetPost ? TIME_DATE_MONTH_WITH_COMMA : TIME_DATE_FORMAT_WITH_COMMA;
  const attachStyles = isMobile ? attachmentMobileStyles : attachmentStyles;
  const authorName = getFullName({
    firstName: author.first_name,
    lastName: author.last_name,
  });

  const { post: pollFromPost } = usePostDetailsPage(post?.meta?.poll_id || '');

  const categoryTags = post.category_tags?.map((tag) => {
    const category = categoriesList.find((cat) => cat.id === tag.id);
    if (category) {
      return category;
    } else {
      return { ...tag, icon: 'untagged' };
    }
  });

  const formattedBirthDate = post?.meta?.birth_date
    ? formattedDate(new Date(post.meta.birth_date), MONTH_DAY_SHORT)
    : '';

  const onNavigateToNewUser = () => {
    navigate(
      generatePath(Links.we.employees.employee.index, {
        employeeId: authorId,
      }),
    );
  };

  function handleLinkClick(e: SyntheticEvent) {
    e.stopPropagation();
    navigate(Links.we.kpi.index);
  }

  if (isLoading && targetId) {
    return <Spinner />;
  }

  // TODO: refactoring: Move blocks to its components
  // - kpi post details
  // isWelcome post details
  return (
    <ContainerW>
      <DetailsCard>
        {/* Author section at top */}
        {!isWelcomePost && !isKpiTargetPost && !isBirthdayPost && !isPollPost && (
          <AuthorSection>
            <AuthorMeta>
              <Avatar
                size={isMobile ? 'xs' : 's'}
                userId={authorId}
                filename={avatar?.small || ''}
              />
              <AuthorInfo>
                <AuthorName>
                  {getFullName({ firstName: first_name, lastName: last_name })}
                </AuthorName>
                <DateTime>{formattedDate(new Date(published_at), dateFormat)}</DateTime>
              </AuthorInfo>
            </AuthorMeta>
            <>
              <PostActionsW>
                {canEdit && (
                  <CreateButton
                    startIcon={
                      <CreateIcon
                        style={{
                          fill: theme.palette.common.white,
                          marginLeft: isMobile ? '8px' : '0px',
                          marginRight: isMobile ? '0px' : '8px',
                        }}
                      />
                    }
                    onClick={onEdit}
                    style={{
                      height: '56px',
                      padding: isMobile ? '0px' : '16px 24px',
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.common.white,
                      borderRadius: '8px',
                      fontWeight: 400,
                      textTransform: 'none',
                    }}
                  >
                    {!isMobile &&
                      (isTaskPost ? t('tasks.details.editButton') : t('post.details.editButton'))}
                  </CreateButton>
                )}
                {showDeleteButton && canDelete && (
                  <CreateButton
                    startIcon={
                      <DeleteIcon
                        style={{
                          marginLeft: isMobile ? '8px' : '0px',
                          marginRight: isMobile ? '0px' : '8px',
                        }}
                      />
                    }
                    onClick={onDelete}
                    style={{
                      height: '56px',
                      padding: isMobile ? '0px' : '16px 24px',
                      backgroundColor: theme.palette.common.white,
                      color: theme.palette.primary.main,
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontWeight: 400,
                      textTransform: 'none',
                    }}
                  >
                    {!isMobile &&
                      (isTaskPost
                        ? t('tasks.details.removeButton')
                        : t('post.details.removeButton'))}
                  </CreateButton>
                )}
              </PostActionsW>
            </>
          </AuthorSection>
        )}
        {isWelcomePost && !isKpiTargetPost && !isBirthdayPost && <></>}
        {/* Category tags — hidden for task posts */}
        {!!categoryTags?.length && !isTaskPost && (
          <TagContainer>
            {(isMobile ? categoryTags.slice(0, MOBILE_CATEGORIES_LIMIT) : categoryTags).map(
              (categoryTag) => (
                <TagW key={categoryTag.id}>
                  <Tag
                    sx={{
                      backgroundColor: theme.palette.info.light,
                      color: '#000',
                      height: '36px',
                      width: '220px',
                      margin: 0,
                    }}
                  >
                    {categoryTag.title}
                  </Tag>
                </TagW>
              ),
            )}
            {isMobile && categoryTags.length > MOBILE_CATEGORIES_LIMIT && (
              <MoreBadge onClick={handleOpenCategoriesModal}>
                +{categoryTags.length - MOBILE_CATEGORIES_LIMIT} more
              </MoreBadge>
            )}
          </TagContainer>
        )}

        {title && <PostTitle>{title}</PostTitle>}

        {/* Task-specific fields: Priority and Assignees */}
        {isTaskPost && post.task?.priority && (
          <TaskFieldSection>
            <TaskFieldLabel>{t('tasks.details.priorityLabel')}</TaskFieldLabel>
            <TaskPriorityChip priority={post.task.priority}>{post.task.priority}</TaskPriorityChip>
          </TaskFieldSection>
        )}

        {isTaskPost && post.task?.progress !== undefined && (
          <TaskFieldSection>
            <TaskFieldLabel>{t('task.create.form.placeholders.progress')}</TaskFieldLabel>
            <ProgressBarContainer>
              <ProgressTrack>
                <ProgressFill value={post.task.progress ?? 0} />
              </ProgressTrack>
              <ProgressLabel>{post.task.progress ?? 0}%</ProgressLabel>
            </ProgressBarContainer>
          </TaskFieldSection>
        )}

        {isTaskPost && !!post.assigned_to?.length && (
          <TaskFieldSection
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAssigneesModal();
            }}
          >
            <TaskFieldLabel>{t('tasks.details.assigneesLabel')}</TaskFieldLabel>
            <AvatarGroup
              max={isMobile ? 8 : 20}
              sx={{
                justifyContent: 'flex-end',
                '& .MuiAvatar-root': { width: 40, height: 40, fontSize: '0.875rem' },
              }}
            >
              {post.assigned_to.map((user) => (
                <Avatar
                  key={user.id}
                  size="s"
                  userId={user.id}
                  filename={user?.avatar?.small}
                  withNavigation={false}
                  initials={
                    user.first_name || user.last_name
                      ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`
                      : undefined
                  }
                  sx={{
                    width: 40,
                    height: 40,
                    ...{ background: theme.palette.info.main },
                  }}
                  wrapperSx={{ marginLeft: '-8px' }}
                />
              ))}
            </AvatarGroup>
          </TaskFieldSection>
        )}

        {isTaskPost && description && (
          <TaskFieldSection>
            <TaskFieldLabel>{t('tasks.details.descriptionLabel')}</TaskFieldLabel>
            <TaskFieldValue>
              <div dangerouslySetInnerHTML={{ __html: description }} />
            </TaskFieldValue>
          </TaskFieldSection>
        )}

        {!isTaskPost && (
          <DescriptionW>
            {isWelcomePost && (
              <Trans
                i18nKey="post.listItem.welcomePost.description"
                values={{ linkLabel: authorName }}
              >
                We have some great news to share. Our family grows! Take a chance to say hello to{' '}
                <LinkW
                  onClick={(e: { stopPropagation: () => void }) => {
                    e.stopPropagation();
                    onNavigateToNewUser();
                  }}
                >
                  {authorName}
                </LinkW>
              </Trans>
            )}
            {isBirthdayPost && (
              <Trans
                i18nKey={
                  formattedBirthDate
                    ? 'post.listItem.birthdayPost.description'
                    : 'post.listItem.birthdayPost.oldDescription'
                }
                values={{ authorName, formattedBirthDate }}
              >
                {' '}
                It&quote;s
                <LinkW
                  onClick={(e: { stopPropagation: () => void }) => {
                    e.stopPropagation();
                    onNavigateToNewUser();
                  }}
                >
                  {authorName}
                </LinkW>
                birthday today! Congratulations! 🥳
              </Trans>
            )}
            {isPollPost && pollFromPost && (
              <PollDescription>{t('post.listItem.pollPost.description')}</PollDescription>
            )}
            {isKpiTargetPost && (
              <>
                <span>
                  {t('completedKpiTargetPost.description1')}{' '}
                  {t('completedKpiTargetPost.description2')}
                </span>
                <TargetW>
                  {data?.data && !isLoading && <TargetItem {...data?.data} isFromPost />}
                </TargetW>
                <div>
                  <LinkW onClick={handleLinkClick}>{t('completedKpiTargetPost.link')}</LinkW>
                </div>
              </>
            )}
            <div
              dangerouslySetInnerHTML={{
                __html: (!isWelcomePost && !isKpiTargetPost && description) || '',
              }}
            />
          </DescriptionW>
        )}

        {isTaskPost && post.task?.deadline && (
          <TaskFieldSection>
            <TaskFieldLabel>{t('tasks.details.deadlineLabel')}</TaskFieldLabel>
            <TaskFieldValue>{format(new Date(post.task.deadline), 'dd/MM/yyyy')}</TaskFieldValue>
          </TaskFieldSection>
        )}

        {isWelcomePost && <WelcomeImage src={welcomePost} alt="welcome post image" />}
        {isBirthdayPost && <BirthdayImage src={birthdayPost} alt="birthday post image" />}

        {poll && (
          <PollOptions {...poll} postId={id} isDetails hasMarginTop={!!attachments?.length} />
        )}
        {isPollPost && pollFromPost?.poll?.options && (
          <PollResults options={pollFromPost.poll?.options} />
        )}

        {docsList.length > 0 && (
          <SectionW>
            <SectionLabel>{t('post.details.documentsLabel')}</SectionLabel>
            <DocsGridW>
              {(isMobile ? docsList.slice(0, MOBILE_DOCS_LIMIT) : docsList).map((doc) => {
                const { id: docId, file, file_url } = doc;
                const fileName =
                  file.original.length > DETAILS_FILE_NAME_LENGTH_LIMIT
                    ? hideDocNameMidPart({
                        docName: file.original,
                        limit: DETAILS_FILE_NAME_LENGTH_LIMIT,
                      })
                    : file.original;
                return (
                  <DocumentAttachment
                    key={docId}
                    postId="null"
                    attachmentId={docId}
                    fileName={fileName}
                    file_extension={doc.file_extension}
                    handleDelete={() => null}
                    hasDownload
                    originalFileName={file.original}
                    originalFileUrl={file_url?.original}
                    blockDelete
                  />
                );
              })}
            </DocsGridW>
            {isMobile && docsList.length > MOBILE_DOCS_LIMIT && (
              <SeeMoreLink onClick={handleOpenDocsModal}>{t('common.seeAll')}</SeeMoreLink>
            )}
          </SectionW>
        )}

        {(imagesList.length > 0 || videosList.length > 0) && (
          <MediaSectionW>
            {imagesList.length > 0 && (
              <MediaColumnW>
                <SectionLabel>{t('post.details.imagesLabel')}</SectionLabel>
                <AttachmentsW>
                  {(isMobile ? imagesList.slice(0, MOBILE_IMAGES_LIMIT) : imagesList).map(
                    ({ id: attachmentId, file, file_url }) => (
                      <ListItemImageAttachment
                        postId={id}
                        key={attachmentId}
                        attachmentId={attachmentId}
                        fileName={file.small}
                        fileUrl={file_url?.small}
                        wrapperSx={attachStyles}
                        onOpenGallery={handleOpenModalGallery}
                      />
                    ),
                  )}
                </AttachmentsW>
                {isMobile && imagesList.length > MOBILE_IMAGES_LIMIT && (
                  <SeeMoreLink onClick={handleOpenImagesModal}>{t('common.seeAll')}</SeeMoreLink>
                )}
              </MediaColumnW>
            )}
            {videosList.length > 0 && (
              <MediaColumnW>
                <SectionLabel>{t('post.details.videosLabel')}</SectionLabel>
                <AttachmentsW>
                  {(isMobile ? videosList.slice(0, MOBILE_VIDEOS_LIMIT) : videosList).map(
                    ({ id: attachmentId, file_url }) => (
                      <VideoAttachment
                        key={attachmentId}
                        onOpenGallery={handleOpenModalVideoGallery}
                        postId={id}
                        attachmentId={file_url.video_uid}
                        wrapperSx={attachStyles}
                        blockDelete
                      />
                    ),
                  )}
                </AttachmentsW>
                {isMobile && videosList.length > MOBILE_VIDEOS_LIMIT && (
                  <SeeMoreLink onClick={handleOpenVideosModal}>{t('common.seeAll')}</SeeMoreLink>
                )}
              </MediaColumnW>
            )}
          </MediaSectionW>
        )}

        <ReactionsWrapper
          id={id}
          isPreview={true}
          reactions={reactions_stats}
          handleOpenReactionModal={handleOpenModalReaction}
        />
      </DetailsCard>

      {(isWelcomePost || isKpiTargetPost || isBirthdayPost || isPollPost) && (
        <FooterW>
          <DateTime>{formattedDate(new Date(published_at), dateFormat)}</DateTime>
        </FooterW>
      )}

      {isOpenGallery && (
        <ImageGallery
          open={isOpenGallery}
          onClose={handleCloseModalGallery}
          postId={id}
          imageList={imagesList}
        />
      )}
      {isOpenVideoGallery && (
        <VideoGallery
          open={isOpenVideoGallery}
          onClose={handleCloseModalVideoGallery}
          postId={id}
          videoList={videosList}
        />
      )}

      {isOpenReaction && (
        <ReactionConfirmDialog
          title={t('reactions.title')}
          onClose={handleCloseModalReaction}
          postId={id}
          open={isOpenReaction}
        />
      )}

      {isOpenCategoriesModal && (
        <SeeMoreModal
          open={isOpenCategoriesModal}
          onClose={handleCloseCategoriesModal}
          title={t('post.details.categoriesLabel')}
        >
          <TagContainer
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: 0,
            }}
          >
            {categoryTags?.map((categoryTag) => (
              <Tag
                key={categoryTag.id}
                sx={{
                  backgroundColor: theme.palette.info.light,
                  color: '#000',
                  height: '36px',
                  width: '100%',
                  margin: 0,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                {categoryTag.title}
              </Tag>
            ))}
          </TagContainer>
        </SeeMoreModal>
      )}

      {isOpenDocsModal && (
        <SeeMoreModal
          open={isOpenDocsModal}
          onClose={handleCloseDocsModal}
          title={t('post.details.documentsLabel')}
        >
          <DocsGridW>
            {docsList.map((doc) => {
              const { id: docId, file, file_url } = doc;
              const fileName =
                file.original.length > DETAILS_FILE_NAME_LENGTH_LIMIT
                  ? hideDocNameMidPart({
                      docName: file.original,
                      limit: DETAILS_FILE_NAME_LENGTH_LIMIT,
                    })
                  : file.original;
              return (
                <DocumentAttachment
                  key={docId}
                  postId="null"
                  attachmentId={docId}
                  fileName={fileName}
                  handleDelete={() => null}
                  hasDownload
                  originalFileName={file.original}
                  originalFileUrl={file_url?.original}
                  file_extension={doc.file_extension}
                  blockDelete
                />
              );
            })}
          </DocsGridW>
        </SeeMoreModal>
      )}

      {isOpenImagesModal && (
        <SeeMoreModal
          open={isOpenImagesModal}
          onClose={handleCloseImagesModal}
          title={t('post.details.imagesLabel')}
        >
          <AttachmentsW>
            {imagesList.map(({ id: attachmentId, file, file_url }) => (
              <ListItemImageAttachment
                postId={id}
                key={attachmentId}
                attachmentId={attachmentId}
                fileName={file.small}
                fileUrl={file_url?.small}
                wrapperSx={attachStyles}
                onOpenGallery={handleOpenModalGallery}
              />
            ))}
          </AttachmentsW>
        </SeeMoreModal>
      )}

      {isOpenVideosModal && (
        <SeeMoreModal
          open={isOpenVideosModal}
          onClose={handleCloseVideosModal}
          title={t('post.details.videosLabel')}
        >
          <AttachmentsW>
            {videosList.map(({ id: attachmentId, file_url }) => (
              <VideoAttachment
                key={attachmentId}
                onOpenGallery={handleOpenModalVideoGallery}
                postId={id}
                attachmentId={file_url.video_uid}
                wrapperSx={attachStyles}
              />
            ))}
          </AttachmentsW>
        </SeeMoreModal>
      )}

      {isOpenAssigneesModal && (
        <SeeMoreModal
          open={isOpenAssigneesModal}
          onClose={handleCloseAssigneesModal}
          title="Task Assigness"
        >
          <AssigneeListW>
            {post?.assigned_to?.map((user) => (
              <AssigneeRow key={user.id}>{`${user.first_name} ${user.last_name}`}</AssigneeRow>
            ))}
          </AssigneeListW>
        </SeeMoreModal>
      )}
    </ContainerW>
  );
}
